import React, { useCallback, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import api from "../../services/api";
import GenericUpper from "../../components/generic-upper";
import { Container } from "react-grid-system";
import TableWithLabelBlackV2 from '../../components/TableWithLabelBlack/indexV2';
import TextOrStatus from "../../components/textOrStatus";
import IconOrder from '../../assets/icons/icon-order.png';
import status3 from "../../assets/icons/icon-status-enviado.svg";
import { store } from "react-notifications-component";
import { TaglineP } from "../../styles/stylesTypography";
import { theme } from "../../styles/theme";
import downloadFile from "../../utils/downloadFile";
import identifyErrorMenssage from "../../services/identifyErrorMenssage";

const STATUS_FILTER_OPTIONS = [
  {
    label: "ATIVO",
    value: "Ativo",
    color: "#03903E",
    backgroundColor: "#EBF9F1",
    icon: status3,
    padding: "5px 8px",
    minHeight: "28px",
  },
  {
    label: "INATIVO",
    value: "Inativo",
    color: "#FF3B30",
    backgroundColor: "#FFEDEE",
    dotColor: "#FF5A52",
    padding: "5px 8px",
    minHeight: "28px",
  },
];

const FILTER_FIELD_MAP = {
  status: "status",
  nome: "nome",
  documento: "documento",
  tipo: "tipo",
  ufRegistro: "estado",
};

const normalizeApiFilterValue = (value) => String(value ?? "").trim();
const isEmptyMode = (mode) => mode === "isEmpty" || mode === "isNotEmpty";
const mapStatusFilterValueToApi = (value) => {
  const normalized = normalizeApiFilterValue(value).toLowerCase();
  if (normalized === "ativo" || normalized === "1") return 1;
  if (normalized === "inativo" || normalized === "0") return 0;
  return normalizeApiFilterValue(value);
};

const buildConstraint = (matchMode, value) => {
  const normalizedValue = normalizeApiFilterValue(value);
  if (!isEmptyMode(matchMode) && !normalizedValue) return null;
  return { matchMode, value: normalizedValue };
};

const buildApiFiltersPayload = (columnFilters = {}) => {
  return Object.entries(columnFilters).reduce((acc, [columnKey, state]) => {
    const apiField = FILTER_FIELD_MAP[columnKey];
    if (!apiField || !state) return acc;

    if (columnKey === "status") {
      const selectedValues = Array.isArray(state.selectedValues)
        ? state.selectedValues
          .map((value) => mapStatusFilterValueToApi(value))
          .filter((value) => value !== null && value !== undefined && value !== "")
        : [];
      if (selectedValues.length === 0) return acc;

      acc[apiField] = {
        operator: selectedValues.length > 1 ? "or" : "and",
        constraints: selectedValues.map((value) => ({ matchMode: "contains", value })),
      };
      return acc;
    }

    const constraints = [];
    const sharedValueForEmptyMode = isEmptyMode(state.mode)
      ? (state.secondaryValue ?? state.value)
      : state.value;
    const primaryConstraint = buildConstraint(state.mode, sharedValueForEmptyMode);
    if (primaryConstraint) constraints.push(primaryConstraint);

    const secondaryConstraint = buildConstraint(state.secondaryMode, state.secondaryValue);
    if (secondaryConstraint) constraints.push(secondaryConstraint);
    if (constraints.length === 0) return acc;

    acc[apiField] = {
      operator: state.combine === "or" ? "or" : "and",
      constraints,
    };
    return acc;
  }, {});
};

const getArtsNacional = async ({ limit = 100, offset = 0, filters = {} }) => {
  const payload = { search: "", estado: "NACIONAL", filters };
  const response = await api.post(`/art/pesquisa?limit=${limit}&offset=${offset}`, payload);
  const data = response?.data?.data;
  const arts = data?.arts || data || [];
  return Array.isArray(arts) ? arts : [];
};

const getRowStatusLabel = (row) => ((row?.status === 1 || row?.crea?.status === 1) ? "Ativo" : "Inativo");
const normalizeFilterValue = (value) => String(value ?? "").trim().toLowerCase();

const getColumnFilterValue = (row, columnKey) => {
  switch (columnKey) {
    case "status":
      return getRowStatusLabel(row);
    case "nome":
      return row?.crea?.agronomo?.nome || "";
    case "documento":
      return row?.crea?.crea || "";
    case "numero":
      return row?.numero_art || "";
    case "tipo":
      return row?.crea?.tipo || "";
    case "ufRegistro":
      return row?.crea?.estado || row?.estado || "NACIONAL";
    default:
      return "";
  }
};

const applyArtsFilters = (items, columnFilters) => {
  if (!columnFilters || Object.keys(columnFilters).length === 0) return items;

  const matchText = (rowValue, mode, filterValue) => {
    const normalizedRow = normalizeFilterValue(rowValue);
    const normalizedFilter = normalizeFilterValue(filterValue);
    if (!normalizedFilter) return true;

    switch (mode) {
      case "equals":
        return normalizedRow === normalizedFilter;
      case "notEquals":
        return normalizedRow !== normalizedFilter;
      case "startsWith":
        return normalizedRow.startsWith(normalizedFilter);
      case "endsWith":
        return normalizedRow.endsWith(normalizedFilter);
      case "notContains":
        return !normalizedRow.includes(normalizedFilter);
      case "contains":
      default:
        return normalizedRow.includes(normalizedFilter);
    }
  };

  return items.filter((row) => (
    Object.entries(columnFilters).every(([columnKey, state]) => {
      if (!state) return true;
      const rowValue = getColumnFilterValue(row, columnKey);

      if (columnKey === "status") {
        const selectedValues = Array.isArray(state.selectedValues) ? state.selectedValues : [];
        if (selectedValues.length === 0) return true;
        const normalizedRow = normalizeFilterValue(rowValue);
        return selectedValues.some((value) => normalizedRow.includes(normalizeFilterValue(value)));
      }

      return matchText(rowValue, state.mode, state.value);
    })
  ));
};

const ExportarDefesasTrtDetails = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState({});
  const lastFilterKeyRef = useRef("");
  const requestSeqRef = useRef(0);

  const loadItems = useCallback(async (nextColumnFilters = {}, showLoader = true) => {
    const requestId = ++requestSeqRef.current;
    if (showLoader) setLoading(true);
    try {
      const filters = buildApiFiltersPayload(nextColumnFilters);
      const arts = await getArtsNacional({ limit: 100, offset: 0, filters });
      if (requestId !== requestSeqRef.current) return;
      setItems(applyArtsFilters(arts, nextColumnFilters));
    } catch (e) {
      if (requestId !== requestSeqRef.current) return;
      setItems([]);
    }
    if (showLoader && requestId === requestSeqRef.current) setLoading(false);
  }, []);

  React.useEffect(() => {
    loadItems({}, true);
  }, [loadItems]);

  const handleColumnFiltersChange = useCallback((nextFilters) => {
    setColumnFilters((previousFilters) => {
      const previousKey = JSON.stringify(previousFilters ?? {});
      const nextKey = JSON.stringify(nextFilters ?? {});
      return previousKey === nextKey ? previousFilters : (nextFilters ?? {});
    });
  }, []);

  React.useEffect(() => {
    const nextKey = JSON.stringify(columnFilters || {});
    const previousKey = lastFilterKeyRef.current;
    if (nextKey === previousKey) return;
    lastFilterKeyRef.current = nextKey;
    const hasActiveFiltersNow = nextKey !== "{}" && nextKey !== "";
    const hadActiveFiltersBefore = previousKey !== "" && previousKey !== "{}";
    if (!hasActiveFiltersNow && !hadActiveFiltersBefore) return;

    const timer = setTimeout(() => {
      loadItems(columnFilters, true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [columnFilters, loadItems]);

  const onSelectRow = useCallback(async (id) => {
    try {
      const response = await api.get(`/arts/relatorio/${id}`);
      const url = response?.data?.data;
      if (url) {
        downloadFile(url, 'TRT.pdf');
      }
    } catch (e) {
      store.addNotification({
        title: "Erro!",
        message: identifyErrorMenssage(e, "ERRO-661 Erro ao gerar relatório da TRT - Tente novamente, se o erro persistir acione o suporte."),
        type: "danger",
        insert: "top",
        container: "top-right",
        animationIn: ["animated", "fadeIn"],
        animationOut: ["animated", "fadeOut"],
        dismiss: { duration: 4000, onScreen: true },
      });
    }
  }, []);

  const columns = useMemo(() => ([
    {
      key: "status",
      label: "Status",
      minWidth: "70px",
      width: "120px",
      maxWidthContent: "120px",
      enableHorizontalScroll: true,
      filterType: "checkbox",
      filterPanelWidth: "200px",
      filterOptions: STATUS_FILTER_OPTIONS,
      filterBy: (row) => getRowStatusLabel(row),
      render: (row) => (
        <TextOrStatus
          maxWidth="auto"
          type="status"
          status={(row?.status === 1 || row?.crea?.status === 1) ? 2 : 3}
          textSuccess="Ativo"
          textError="Inativo"
          textDefault="Ativo"
        />
      ),
    },
    {
      key: "nome",
      label: "Nome",
      minWidth: "200px",
      width: "100%",
      filterBy: (row) => row?.crea?.agronomo?.nome || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
          {row?.crea?.agronomo?.nome || "-"}
        </TaglineP>
      ),
    },
    {
      key: "documento",
      label: "Documento",
      minWidth: "150px",
      width: "170px",
      filterBy: (row) => row?.crea?.crea || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {row?.crea?.crea || "-"}
        </TaglineP>
      ),
    },
    {
      key: "tipo",
      label: "Tipo",
      minWidth: "100px",
      width: "120px",
      filterBy: (row) => row?.crea?.tipo || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {(row?.crea?.tipo || "-")?.toString().toUpperCase()}
        </TaglineP>
      ),
    },
    {
      key: "numero",
      label: "Número TRT",
      minWidth: "120px",
      width: "140px",
      filterBy: (row) => row?.numero_art || "",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {row?.numero_art || "-"}
        </TaglineP>
      ),
    },
    {
      key: "ufRegistro",
      label: "UF/Registro",
      minWidth: "110px",
      width: "130px",
      filterBy: (row) => row?.crea?.estado || row?.estado || "NACIONAL",
      render: (row) => (
        <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={600} style={{ overflowWrap: "anywhere" }}>
          {(row?.crea?.estado || row?.estado || "NACIONAL")?.toString().toUpperCase()}
        </TaglineP>
      ),
    },
  ]), []);

  return (
    <>
      <GenericUpper title="Exportar Defesas - Nacional" backLink="/exportar-defesas" />
      <Container style={{ paddingLeft: "32px", paddingRight: "32px" }}>
        <div style={{ maxWidth: '820px', margin: '16px auto 8px', textAlign: 'center' }}>
          <TaglineP color={theme.colors.gray.Cinza_2} fontWeight={800}>
            Selecione a TRT para gerar o relatório nacional (CFTA) conforme o período.
          </TaglineP>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>
            <a
              href="https://www.cfta.org.br/index.php/trt/tutoriais-cfta/trt-cargo-funcao/324-como-preencher-o-trt-de-cargo-ou-funcao"
              target="_blank"
              rel="noreferrer noopener"
            >
              Ver guia de envio e orientações
            </a>
          </p>
        </div>

        <TableWithLabelBlackV2
          columns={columns}
          data={items}
          showAll={true}
          itemsPerPage={items.length}
          serverSide={true}
          isLoading={loading}
          enableColumnFilters={true}
          useServerSideColumnFilters={true}
          onColumnFiltersChange={handleColumnFiltersChange}
          enableDefaultRowNavigation={false}
          emptyMessage="Não há resultados"
          actionsColumn={{
            enabled: true,
            position: "first",
            label: "AÇÕES",
            width: "100%",
            minWidth: "80px",
            maxWidth: "80px",
            justifyContent: "flex-start",
            buttons: [
              {
                key: "report",
                icon: IconOrder,
                iconAlt: "Ícone de relatório",
                tooltip: "Gerar relatório",
                ariaLabel: "Gerar relatório",
                onClick: (row) => onSelectRow(row?.id),
              },
            ],
          }}
          getRowId={(row) => row?.id}
        />
      </Container>
    </>
  );
};

export default withRouter(ExportarDefesasTrtDetails);
